import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TimelineLinkTargetOption } from "@/features/timeline";

type TimelineLinkPickerProps = {
  disabled?: boolean;
  onAddLink: (option: TimelineLinkTargetOption) => Promise<void> | void;
  options: TimelineLinkTargetOption[];
};

function encodeOptionValue(option: TimelineLinkTargetOption) {
  return `${option.kind}::${option.id}`;
}

export function TimelineLinkPicker({
  disabled = false,
  onAddLink,
  options,
}: TimelineLinkPickerProps) {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const selectedOption = useMemo(
    () => options.find((option) => encodeOptionValue(option) === selectedValue) ?? null,
    [options, selectedValue],
  );

  async function handleAdd() {
    if (!selectedOption || submitting) {
      return;
    }

    setSubmitting(true);
    try {
      await onAddLink(selectedOption);
      setSelectedValue("");
    } finally {
      setSubmitting(false);
    }
  }

  if (options.length === 0) {
    return (
      <div className="rounded-[22px] border border-dashed border-border/80 bg-secondary/35 p-4 text-sm text-muted-foreground">
        {t("timeline.links.noOptions")}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Select
        disabled={disabled || submitting}
        onValueChange={setSelectedValue}
        value={selectedValue}
      >
        <SelectTrigger aria-label={t("timeline.links.pickerLabel")}>
          <SelectValue placeholder={t("timeline.links.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={encodeOptionValue(option)} value={encodeOptionValue(option)}>
              {t(option.kindLabelKey)}: {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs leading-5 text-muted-foreground">
          {selectedOption
            ? `${t(selectedOption.kindLabelKey)} / ${selectedOption.secondaryText}`
            : t("timeline.links.helper")}
        </p>
        <Button
          disabled={disabled || submitting || !selectedOption}
          onClick={() => void handleAdd()}
          type="button"
          variant="outline"
        >
          {t("timeline.links.addAction")}
        </Button>
      </div>
    </div>
  );
}
